import {Module} from "vuex";
import {IDBase, QueryFacade} from "@/controller/QueryFacade";
import {ExchangeRateSnapshot, HasRightResponse, HasRightsRequest, loadExchangeRates, Rate} from "@/store/CurrencyStore";
import {AsyncJavaRequest, Main} from "@/controller/main";
import {AddressFieldWithKeyColumn, DeliveryAddressesState, loadAddresses} from "@/store/AddressStore";
import {loadExportConfig} from "@/store/ExportConfigStore";
import {Columns, ReplaceRequest} from "@/controller/ReplaceRequest";
import {SimpleComponentLine} from "@/store/ComponentImportStore";

/*
    Buildphase
    MRDDate
    Receiving Location 1
    Receiving Location 2
    Receiving Location 3

    Ford Supplier code
    Customs Clearance
    Buyer
    Disponent/ Requester
    Customer

    Quotation No.
    Quotation Request Date
    Supplier Shipping Doc. No.
    Shipping Date
    Purchase Order No.
*/
export class PPanelHeader {
    public MRDDate: string = "";
    public ReceivingLocation1: string = "";
    public ReceivingLocation2: string = "";
    public ReceivingLocation3: string = "";

    public FordSupplierCodeFpc: string = "EMDSA";
    public CustomsClearance: string = "";
    public Buyer: string = "";
    public DisponentRequester: string = "";
    public Customer: string = "";

    public QuotationNo: string = "";
    public QuotationRequestDate: string = "";
    public SupplierShippingDocNo: string = "";
    public ShippingDate: string = "";
    public PurchaseOrderNo: string = "";
}

/*
    PART No
    SFX
    CAD Ver.
    Release Notice
    PART NAME
    DIESET No
    Die Set Ship
    Available at Plant
    Part Quantity
    Quality of Parts
    Receiving Date @ Ford
    Receiving Location [1] from Header Table

    Manufacturing
    Labour
    Material
    Laser
    Total 1
    Laser Setup
    Press Setup
    CMM
    Package
    Total 2
    Transport Costs/ pcs
    Total Costs Offer
    Remarks RFQ

    Date parts shipped from Supplier Location
    Status P/S/A
    Date of parts receiving at Ford Location
    Status P/S/A
    Part Submission Warrent No.(CMM Report No.)
    Date stamp incorporated in everypart?
    Label attached to every single part?
    WERS Reslease No.
    Stamping Plant/Production Source
    PNNR (Purchase/ order no.)
    QTY Supplier
    Offer Sfx.
    Ship Sfx.
    QTY Shipping
    ICBA No.
    QTY Plant
    QTY EX75
    QTY Prot
    Receiv. Sfx.
    QTY Receiv.
    Remarks Shipping
 */

export class ProcuranceLine {
    public readonly id: number;
    public PARTNo: string = "";
    public SFX: string = "";
    public CADVer: string = "";
    public ReleaseNotice: string = "";
    public PARTNAME: string = "";
    public DIESETNo: string = "";
    public DieSetShip: string = "";
    public AvailableAtPlant: string = "";
    public PartQuantity: number = 0;
    public QualityOfParts: string = "";
    public ReceivingDateAtFord: string = "";
    public ReceivingLocationFromHeaderTable: string = "";

    public Manufacturing: string = "";
    public Labour: string = "";
    public Material: string = "";
    public Laser: string = "";
    public Total1: string = "";
    public LaserSetup: string = "";
    public PressSetup: string = "";
    public CMM: string = "";
    public Package: string = "";
    public Total2: number|string = 0;
    public TransportCostsPcs: string = "";
    public TotalCostsOffer: number|string = 0;
    public RemarksRFQ: string = "";

    public DatePartsShippedFromSupplierLocation: string = "";
    public StatusPSAShipped: string = "";
    public DatePartsReceivingAtFordLocation: string = "";
    public StatusPSAReceived: string = "";
    public PartSubmissionWarrentNo: string = "";
    public DateStampIncorporatedInEverypart: string = "";
    public LabelAttachedToEverySinglePart: string = "";
    public WERSResleaseNo: string = "";
    public StampingPlantProductionSource: string = "";
    public PNNR: string = "";
    public QTYSupplier: string = "";
    public OfferSfx: string = "";
    public ShipSfx: string = "";
    public QTYShipping: string = "";
    public ICBANo: string = "";
    public QTYPlant: string = "";
    public QTYEX75: string = "";
    public QTYProt: string = "";
    public ReceivSfx: string = "";
    public QTYReceiv: string = "";
    public RemarksShipping: string = "";

    constructor(id: number) {
        this.id = id;
    }

}

export enum PPanelFreezeState {
    NONE = "NONE",
    RFQ = "RFQ",
    SHIPPING = "SHIPPING",
    FINISH = "FINISH"
}

export class PPanelBuildPhase {
    public header: PPanelHeader = new PPanelHeader();
    public dataLines: ProcuranceLine[] = [];
    public freeze: PPanelFreezeState = PPanelFreezeState.NONE;
    public name: string = "";
}

export class PPanelData implements IDBase {
    public id: string = "";
    public buildPhases: { [name: string]: PPanelBuildPhase } = {};
}

interface PPanelRights {
    ppanel_edit_supplier_fields: boolean,
    ppanel_edit_maintainer_fields: boolean,
    ppanel_edit_administrator: boolean,
    singlePpanelFiles_ExportConfigButton_0: boolean,
    singlePpanelFiles_ExportConfigButton_1: boolean,
    singlePpanelFiles_ExportConfigButton_2: boolean,
    singlePpanelFiles_ExportConfigButton_3: boolean,
    singlePpanelFiles_ExportConfigButton_4: boolean,
    singlePpanelFiles_ExportConfigButton_5: boolean,
    singlePpanelFiles_ExportConfigButton_6: boolean,
    ppanel_save:boolean,
    ppanel_freeze:boolean,
}

class PPanelTable2Summarize {
    public Total2 = 0;
    public TotalCostsOffer = 0;
    public TransportCostsPcs = 0;
}

class ExportConfig {
    public label: string = "";
    public fileId: string = "";
}

export class PPanelState {
    public initData: {
        component: SimpleComponentLine
        componentId: string;
    } | undefined = undefined;
    public id: string = "";
    public buildPhase: string = "";
    public buildPhases: string[] = [];
    public header: PPanelHeader = new PPanelHeader();
    public dataLines: ProcuranceLine[] = [];
    public selectedLineId: number | null = null;
    public hoverLineId: number | null = null;
    public currentExchangeRate: ExchangeRateSnapshot | null = null;
    public usedCurrency: string | null = null;
    public readonly addressBlocks: DeliveryAddressesState = new DeliveryAddressesState();
    public data: PPanelData = new PPanelData();
    public singleExports: ExportConfig[] = [];

    public rights: PPanelRights = {
        ppanel_edit_administrator: false,
        ppanel_edit_maintainer_fields: false,
        ppanel_edit_supplier_fields: false,
        singlePpanelFiles_ExportConfigButton_0: false,
        singlePpanelFiles_ExportConfigButton_1: false,
        singlePpanelFiles_ExportConfigButton_2: false,
        singlePpanelFiles_ExportConfigButton_3: false,
        singlePpanelFiles_ExportConfigButton_4: false,
        singlePpanelFiles_ExportConfigButton_5: false,
        singlePpanelFiles_ExportConfigButton_6: false,
        ppanel_save:false,
        ppanel_freeze:false,
    }

    public summarize: PPanelTable2Summarize = new PPanelTable2Summarize();
    public availableQualityOfParts: string[] = [
        "LPG",
        "FPG",
        "NPG 1",
        "NPG 2",
        "NPG 3",
        "NPG 4",
        "NPG 5",
        "NPG 6",
        "NPG 7",
        "NPG 8",
        "NPG 9",
        "NPG 10"
    ];
    public rateToUse: Rate | null = null;

    public freeze: PPanelFreezeState = PPanelFreezeState.NONE;
}

function isAndSetNumeric(object: any | { [key: string]: string }, field: string): boolean {
    let num = object[field];

    if (num && typeof num == "string") {
        num = num.replace(",", ".");
    }

    const n = parseFloat(num);
    if (!isNaN(n) && isFinite(n)) {
        object[field] = parseFloat(n.toFixed(2));
        return true;
    }
    return false;
}

export function invokeReplacementRequest(replaceRequest: ReplaceRequest,finished:(()=>void)|null=null) {
    const origin = Main.getOrigin();
    if (!origin) {
        console.error("sourcehref / origin was not setted, cannot continue");
        return;
    }
    // construct an HTTP request
    const split = origin.split("faces/");
    const url = split[0] + "faces/rst/hierarchy/replace";

    let xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.setRequestHeader("Content-Type", "application/json; charset=UTF-8");
    xhr.responseType = 'blob';
    // send the collected data as JSON
    xhr.send(JSON.stringify(replaceRequest));

    xhr.onload = function (this, event) {
        const blob = this.response;
        const contentDispo = this.getResponseHeader('Content-Disposition');
        // https://stackoverflow.com/a/23054920/
        if (contentDispo !== null) {
            const match = contentDispo.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (match && match.length > 0) {
                const fileName = match[1];
                saveOrOpenBlob(blob, fileName);
                if(finished){
                    finished();
                }
            }
        }
    }

    console.log("ObjectToPost", replaceRequest);
}

export class PPanelStore implements Module<any, unknown> {
    public namespaced = true;
    public state: PPanelState = new PPanelState();

    public getters = {
        dataLines(state: PPanelState): ProcuranceLine[] {
            for (let dataLine of state.dataLines) {
                if (dataLine.ReceivingLocationFromHeaderTable === "") {
                    dataLine.ReceivingLocationFromHeaderTable = state.header.ReceivingLocation1;
                }
            }

            return state.dataLines;
        },
        header(state: PPanelState): PPanelHeader {
            return state.header;
        },
        selectedLineId(state: PPanelState): number | null {
            return state.selectedLineId;
        },
        hoverLineId(state: PPanelState): number | null {
            return state.hoverLineId;
        },
        summarize(state: PPanelState) {
            return state.summarize;
        },
        availableQualityOfParts(state: PPanelState): string[] {
            return state.availableQualityOfParts;
        },
        addressBlocks(state: PPanelState): DeliveryAddressesState {
            return state.addressBlocks;
        },
        rateToUse(state: PPanelState): Rate | null {
            return state.rateToUse;
        },
        buildPhases(state: PPanelState) {
            return state.buildPhases;
        },
        freezeRFQ(state: PPanelState): boolean {
            return state.freeze == PPanelFreezeState.RFQ;
        },
        freezeShipping(state: PPanelState): boolean {
            return state.freeze == PPanelFreezeState.SHIPPING;
        },
        freeze(state: PPanelState): PPanelFreezeState {
            return state.freeze;
        },
        singleExports(state: PPanelState): ExportConfig[] {
            return state.singleExports;
        },
        hasSupplierEditRight(state: PPanelState): boolean {
            return state.freeze != PPanelFreezeState.FINISH &&
                (
                    state.rights.ppanel_edit_supplier_fields ||
                    state.rights.ppanel_edit_administrator
                );
        },
        hasMaintainerEditRight(state: PPanelState): boolean {
            return state.freeze != PPanelFreezeState.FINISH &&
                (
                    state.rights.ppanel_edit_maintainer_fields ||
                    state.rights.ppanel_edit_administrator
                );
        },
        hasAdministratorRight(state: PPanelState): boolean {
            return state.rights.ppanel_edit_administrator;
        },
        hasSaveRight(state: PPanelState): boolean {
            return state.rights.ppanel_save;
        },
        hasFreezeRight(state: PPanelState): boolean {
            return state.rights.ppanel_freeze;
        },
        buildPhase(state: PPanelState): string {
            return state.buildPhase;
        }
    };

    public mutations = {
        addPpanelLine(state: PPanelState) {
            state.dataLines.push(new ProcuranceLine(state.dataLines.length));
        },
        removePpanelLine(state: PPanelState, aLineId: number) {
            for (let i = 0; i < state.dataLines.length; i++) {
                let dataLine = state.dataLines[i];
                if (aLineId === dataLine.id) {
                    state.dataLines.splice(i, 1);
                    return;
                }
            }

        },
        setHoverLine(state: PPanelState, aLine: ProcuranceLine) {
            // console.log("hover: "+aLine.id);
            state.hoverLineId = aLine.id;
        },
        setSelectedLine(state: PPanelState, aLine: ProcuranceLine) {
            // console.log("select: "+aLine.id);
            state.selectedLineId = aLine.id;
        },
        persist(state: PPanelState) {
            const data = state.data;
            if (!data.buildPhases[state.buildPhase]) {
                data.buildPhases[state.buildPhase] = new PPanelBuildPhase();
            }
            data.buildPhases[state.buildPhase].dataLines = state.dataLines;
            data.buildPhases[state.buildPhase].header = state.header;
            data.id = state.id;
            QueryFacade.put("procurance_panel", data);
        },
        setBuildPhase(state: PPanelState, selectedBuildPhase: string) {
            state.buildPhase = selectedBuildPhase;
            state.header = state.data.buildPhases[selectedBuildPhase].header;
            ppanelStore.mutations.updateBuildPhase(state);
        },
        updateBuildPhase(state: PPanelState) {
            const buildPhase = state.data.buildPhases[state.buildPhase];
            state.dataLines = buildPhase.dataLines;
            state.freeze = buildPhase.freeze;
            ppanelStore.mutations.calculateTotals(state);
        },
        freezeRFQ(state: PPanelState) {
            state.data.buildPhases[state.buildPhase].freeze = PPanelFreezeState.RFQ;
            ppanelStore.mutations.updateBuildPhase(state);
        },
        freezeShipping(state: PPanelState) {
            state.data.buildPhases[state.buildPhase].freeze = PPanelFreezeState.SHIPPING;
            ppanelStore.mutations.updateBuildPhase(state);
        },
        removeFreeze(state: PPanelState) {
            state.data.buildPhases[state.buildPhase].freeze = PPanelFreezeState.NONE;
            ppanelStore.mutations.updateBuildPhase(state);
        },
        doFinish(state: PPanelState) {
            state.data.buildPhases[state.buildPhase].freeze = PPanelFreezeState.FINISH;
            ppanelStore.mutations.updateBuildPhase(state);
        },
        async doSingleExport(state: PPanelState, id: string) {
            if (!state || !state.initData) {
                console.error("missing init data for component");
                return;
            }
            const replaceRequest: ReplaceRequest = new ReplaceRequest();
            replaceRequest.fileId = id;

            const parent = await Main.postMessage(
                new AsyncJavaRequest("call", "allComponents",
                    "" + state.initData.component?.parentId));

            const filledData = fillDataForExport(state, parent);
            for (let key of Object.keys(filledData)) {
                replaceRequest.rows=replaceRequest.rows.concat(filledData[key]);
            }

            invokeReplacementRequest(replaceRequest);
        },
        calculateTotals(state: PPanelState) {
            state.summarize = new PPanelTable2Summarize();
            for (let dl of state.dataLines) {
                let total = 0;
                let totalWithTransport = 0;

                if (<any>dl.PartQuantity == "") {
                    dl.PartQuantity = 0;
                }

                if (
                    isAndSetNumeric(dl, "Manufacturing") &&
                    isAndSetNumeric(dl, "Labour") &&
                    isAndSetNumeric(dl, "Material") &&
                    isAndSetNumeric(dl, "Laser")
                ) {
                    total += Number.parseFloat(dl.Manufacturing);
                    total += Number.parseFloat(dl.Labour);
                    total += Number.parseFloat(dl.Material);
                    total += Number.parseFloat(dl.Laser);

                    total = total * Number.parseFloat(<any>dl.PartQuantity);
                    dl.Total1 = total.toFixed(2);


                    if (
                        isAndSetNumeric(dl, "LaserSetup") &&
                        isAndSetNumeric(dl, "PressSetup") &&
                        isAndSetNumeric(dl, "CMM") &&
                        isAndSetNumeric(dl, "Package")
                    ) {
                        total += Number.parseFloat(dl.LaserSetup);
                        total += Number.parseFloat(dl.PressSetup);
                        total += Number.parseFloat(dl.CMM);
                        total += Number.parseFloat(dl.Package);
                        dl.Total2 = Number.parseFloat(total.toFixed(2));


                        if (isAndSetNumeric(dl, "TransportCostsPcs") &&
                            isAndSetNumeric(dl, "PartQuantity")
                        ) {
                            totalWithTransport = (total + Number.parseFloat(dl.TransportCostsPcs));
                            dl.TotalCostsOffer = Number.parseFloat(totalWithTransport.toFixed(2));
                        } else {
                            dl.TotalCostsOffer = 0;
                        }

                    } else {
                        dl.Total2 = 0;
                    }
                } else {
                    dl.Total1 = "";
                }


                state.summarize.Total2 = 0;
                state.summarize.TransportCostsPcs = 0;
                state.summarize.TotalCostsOffer = 0;

                for (let dl of state.dataLines) {
                    if (dl.Total2 && typeof dl.Total2 == "string") {
                        dl.Total2 = <any>(dl.Total2).replace("€", "");
                        isAndSetNumeric(dl, "Total2");
                    }
                    if (dl.TotalCostsOffer && typeof dl.TotalCostsOffer == "string") {
                        dl.TotalCostsOffer = <any>(dl.TotalCostsOffer).replace("€", "");
                        isAndSetNumeric(dl, "TotalCostsOffer");
                    }

                    state.summarize.Total2 += (dl.Total2) ? Number.parseFloat((<any>dl.Total2).toFixed(2)) : 0;
                    state.summarize.TransportCostsPcs += isAndSetNumeric(dl, "TransportCostsPcs") ? Number.parseFloat(dl.TransportCostsPcs) : 0;
                    state.summarize.TotalCostsOffer += (dl.TotalCostsOffer) ? Number.parseFloat((<any>dl.TotalCostsOffer).toFixed(2)) : 0;

                    state.summarize.Total2 = Number.parseFloat(state.summarize.Total2.toFixed(2));
                    state.summarize.TransportCostsPcs = Number.parseFloat(state.summarize.TransportCostsPcs.toFixed(2));
                    state.summarize.TotalCostsOffer = Number.parseFloat(state.summarize.TotalCostsOffer.toFixed(2));


                }

            }
        },

        async loadFromDb(state: PPanelState,component?:SimpleComponentLine) {
            if(state && ppanelStore.state!==state){
                ppanelStore.state = state;
            }

            if(!component) {
                ppanelStore.state.initData = await Main.getInitData();
                if (!ppanelStore.state.initData) {
                    return;
                }
            }
            else{
                ppanelStore.state.initData = {
                    component:component,
                    componentId:component.componentId+"",
                };
            }
            ppanelStore.state.id = (ppanelStore.state.initData)["componentId"];

            console.log("compId: " + ppanelStore.state.id);
            const res = <PPanelData>await QueryFacade.get("procurance_panel", ppanelStore.state.id);
            if (!res) {
                console.info("no data stored for componentId " + ppanelStore.state.id);
                return;
            }
            ppanelStore.state.data = res;
            ppanelStore.state.buildPhases = Object.keys(res.buildPhases);
            ppanelStore.state.buildPhase = ppanelStore.state.buildPhases[ppanelStore.state.buildPhases.length - 1];

            ppanelStore.state.header = res.buildPhases[ppanelStore.state.buildPhase].header;
            ppanelStore.state.dataLines = res.buildPhases[ppanelStore.state.buildPhase].dataLines;

            const deliveryAddressesState = await loadAddresses();
            for (let key in deliveryAddressesState) {
                //@ts-ignore
                ppanelStore.state.addressBlocks[key as keyof DeliveryAddressesState] = deliveryAddressesState[key as keyof DeliveryAddressesState];
            }



            const programId = ppanelStore.state.initData.component.parentId+"";
            const usedCurrency: string|undefined = ppanelStore.state.initData.component.usedCurrency;
            if (!usedCurrency) {
                alert("Please set a currency for that company '"+ppanelStore.state.initData.component.name+"'");
                return;
            } else {
                ppanelStore.state.usedCurrency = usedCurrency;
            }

            const exchangeRateSnapshots = await loadExchangeRates(programId);
            if (exchangeRateSnapshots.length > 0) {
                ppanelStore.state.currentExchangeRate = exchangeRateSnapshots[0];

                let rateToUse = null;
                const BaseRate = "EUR";
                for (let rate of ppanelStore.state.currentExchangeRate.rates) {
                    if (rate.sourceCurrency === usedCurrency && rate.targetCurrency === BaseRate) {
                        rateToUse = rate;
                        break;
                    }
                }
                if (rateToUse !== null) {
                    ppanelStore.state.rateToUse = rateToUse;
                    console.log("usedRate", rateToUse);

                } else {
                    alert("No exchange rate available for " + usedCurrency + " -> " + BaseRate);
                }
            } else {
                alert("No exchange rates defined for that program");
            }

            const request = new HasRightsRequest();
            request.rightTags = [
                "ppanel_edit_supplier_fields",
                "ppanel_edit_maintainer_fields",
                "ppanel_edit_administrator",
                "singlePpanelFiles_ExportConfigButton_0",
                "singlePpanelFiles_ExportConfigButton_1",
                "singlePpanelFiles_ExportConfigButton_2",
                "singlePpanelFiles_ExportConfigButton_3",
                "singlePpanelFiles_ExportConfigButton_4",
                "singlePpanelFiles_ExportConfigButton_5",
                "singlePpanelFiles_ExportConfigButton_6",
                "ppanel_save",
                "ppanel_freeze",
            ];
            const rightResponse: HasRightResponse<PPanelRights> = await Main.postMessage(
                new AsyncJavaRequest("call", "hasRights", JSON.stringify(request))
            );

            state.rights = rightResponse.rights ? rightResponse.rights : {
                ppanel_edit_administrator: false,
                ppanel_edit_maintainer_fields: false,
                ppanel_edit_supplier_fields: false,
                singlePpanelFiles_ExportConfigButton_0: false,
                singlePpanelFiles_ExportConfigButton_1: false,
                singlePpanelFiles_ExportConfigButton_2: false,
                singlePpanelFiles_ExportConfigButton_3: false,
                singlePpanelFiles_ExportConfigButton_4: false,
                singlePpanelFiles_ExportConfigButton_5: false,
                singlePpanelFiles_ExportConfigButton_6: false,
                ppanel_save:false,
                ppanel_freeze:false,
            };

            const exportConfigHead = await loadExportConfig("singlePpanelFiles");
            for (let i = 0; i < exportConfigHead.labels.length; i++) {
                let label = exportConfigHead.labels[i];
                if(((<any>state.rights)["singlePpanelFiles_ExportConfigButton_"+i])) {
                    state.singleExports.push({
                        fileId: label.fileId,
                        label: label.label
                    })
                }
            }

            ppanelStore.mutations.calculateTotals(state);
            ppanelStore.mutations.updateBuildPhase(state);
        }
    };

    constructor() {
    }
}



export function fillDataForExport(state: PPanelState, parent:SimpleComponentLine):{[buildPhaseKey:string]:Columns[]} {
    const res:{[buildPhaseKey:string]:Columns[]} = {};
    for (let buildPhaseId in state.data.buildPhases) {
        const buildPhase: PPanelBuildPhase = state.data.buildPhases[buildPhaseId];

        if(!res[buildPhaseId]){
            res[buildPhaseId]=[];
        }

        for (let dataLine of buildPhase.dataLines) {
            const col: Columns = new Columns()
            res[buildPhaseId].push(col);
            col.a = parent.name;
            col.b = buildPhaseId;
            col.c = buildPhase.header.MRDDate;
            col.d = state.initData?.component?.supplierName;
            col.e = state.addressBlocks.CustomsAddress?.addressBlock?.addressFields[0].address;

            col.f = getAdressBlockByShorthand(state, buildPhase.header.ReceivingLocation1);
            col.g = getAdressBlockByShorthand(state, buildPhase.header.ReceivingLocation2);
            col.h = getAdressBlockByShorthand(state, buildPhase.header.ReceivingLocation3);

            col.i = buildPhase.header.FordSupplierCodeFpc;
            col.j = buildPhase.header.CustomsClearance;
            col.k = buildPhase.header.Buyer;
            col.l = buildPhase.header.DisponentRequester;
            col.m = buildPhase.header.Customer;
            col.n = buildPhase.header.QuotationNo;
            col.o = buildPhase.header.QuotationRequestDate;
            col.p = buildPhase.header.SupplierShippingDocNo;
            col.q = buildPhase.header.ShippingDate;
            col.r = buildPhase.header.PurchaseOrderNo;

            col.s = dataLine.PARTNo;
            col.t = dataLine.SFX;
            col.u = dataLine.CADVer;
            col.v = dataLine.ReleaseNotice;
            col.w = dataLine.PARTNAME;
            col.x = dataLine.DIESETNo;
            col.y = dataLine.DieSetShip;
            col.z = dataLine.AvailableAtPlant;
            col.aa = dataLine.PartQuantity + "";
            col.ab = dataLine.QualityOfParts;
            col.ac = dataLine.ReceivingDateAtFord;
            col.ad = getAdressBlockByShorthand(state, dataLine.ReceivingLocationFromHeaderTable);

            col.ae = convertCurrency(dataLine.Manufacturing);
            col.af = convertCurrency(dataLine.Labour);
            col.ag = convertCurrency(dataLine.Material);
            col.ah = convertCurrency(dataLine.Laser);
            col.ai = convertCurrency(dataLine.Total1);
            col.aj = convertCurrency(dataLine.LaserSetup);
            col.ak = convertCurrency(dataLine.PressSetup);
            col.al = convertCurrency(dataLine.CMM);
            col.am = convertCurrency(dataLine.Package);
            col.an = convertCurrency(dataLine.Total2 + "");
            col.ao = convertCurrency(dataLine.TransportCostsPcs);
            col.ap = convertCurrency(dataLine.TotalCostsOffer + "");
            col.aq = dataLine.RemarksRFQ;

            col.ar = dataLine.DatePartsShippedFromSupplierLocation;
            col.as = dataLine.StatusPSAShipped;
            col.at = dataLine.DatePartsReceivingAtFordLocation;
            col.au = dataLine.StatusPSAReceived;
            col.av = dataLine.PartSubmissionWarrentNo;
            col.aw = dataLine.DateStampIncorporatedInEverypart;
            col.ax = dataLine.LabelAttachedToEverySinglePart;
            col.ay = dataLine.WERSResleaseNo;
            col.az = dataLine.StampingPlantProductionSource;
            col.ba = dataLine.PNNR;
            col.bb = dataLine.QTYSupplier;
            col.bc = dataLine.OfferSfx;
            col.bd = dataLine.ShipSfx;
            col.be = dataLine.QTYShipping;
            col.bf = dataLine.ICBANo;
            col.bg = dataLine.QTYPlant;
            col.bh = dataLine.QTYEX75;
            col.bi = dataLine.QTYProt;
            col.bj = dataLine.ReceivSfx;
            col.bk = dataLine.QTYReceiv;
            col.bl = dataLine.RemarksShipping;

            col.removeUnusedColumns();
        }
    }

    return res;
}

function saveOrOpenBlob(blob: unknown, fileName: string) {
    console.log(fileName)
    const tempEl = document.createElement("a");
    document.body.appendChild(tempEl);
    // @ts-ignore
    tempEl["style"] = "display: none";
    let url = window.URL.createObjectURL(blob);
    tempEl.href = url;
    tempEl.download = "ppanel-report.xlsm";
    tempEl.click();
    window.URL.revokeObjectURL(url);
}


function convertCurrency(value: number | string) {
    if (value !== "" && ppanelStore.state.rateToUse) {
        return (parseFloat("" + value) * parseFloat(ppanelStore.state.rateToUse.exchangeRate)).toFixed(2) + "€"
    }
    return "";
}


function getAdressBlockByShorthand(state: PPanelState, shorthand: string) {
    if (state.addressBlocks.ReceivingLocation.addressBlock.addressFields) {
        const fields: AddressFieldWithKeyColumn[] = <any>state.addressBlocks.ReceivingLocation.addressBlock.addressFields;
        for (let field of fields) {
            if (field.keyColumn == shorthand) {
                return field.address;
            }
        }
    }
    return ""
}

export const ppanelStore = new PPanelStore();